import * as THREE from 'three';
import { element } from '../core/dom';
import { AudioSystem } from '../audio/AudioSystem';
import { InputController } from '../input/InputController';
import { EnvironmentSystem } from '../scene/EnvironmentSystem';
import { EffectsSystem } from '../scene/EffectsSystem';
import { MAPS } from '../scene/assets';
import { createBase, createFieldFrame, createObstacles, createTank } from '../scene/factories';
import { makeMaterial } from '../scene/materials';
import { UIController } from '../ui/UIController';
import { CameraController } from './CameraController';
import { BASE_SHIELD_DURATION, CAMERA_SETTINGS, CELL_SIZE, DIFFICULTY_CONFIG, DROP_MAX_INTERVAL, DROP_MIN_INTERVAL, ENEMY_AGGRO_TIME, ENEMY_BASE_RANGE, ENEMY_BULLDOZER_MOVE_TIME, ENEMY_HEAVY_MOVE_TIME, ENEMY_LAUNCHER_MOVE_TIME, ENEMY_PLAYER_RANGE, ENEMY_SCOUT_MOVE_TIME, FIELD, GRID_COLS, GRID_ROWS, GRID_STEP_TIME, MAX_LEVEL, PALETTE, UPGRADE_BASE_COSTS, WAVE_INTERMISSION_TIME, WEAPONS } from './config';
import { ComboSystem } from './ComboSystem';
import { DropSystem } from './DropSystem';
import { GridMovementSystem } from './GridMovementSystem';
import { NavigationSystem, cellToWorld, worldToCell } from './NavigationSystem';
import { clear as clearProgress, load as loadProgress, save as saveProgress, snapshotFromState } from './persistence';
import { createGameState } from './state';
import type { Drop, DropType, Enemy, EnemyProjectile, EnemyType, GameState, Obstacle, PlayerProjectile, TargetType, TankGroup, UpgradeKind, WeaponKind } from './types';

export class Game {
  private readonly state: GameState = createGameState();
  private readonly audio = new AudioSystem();
  private readonly ui: UIController;
  private readonly aimRaycaster = new THREE.Raycaster();
  private readonly aimPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly aimPoint = new THREE.Vector3();
  private readonly aimVector = new THREE.Vector3();
  private readonly worldUp = new THREE.Vector3(0, 1, 0);
  private readonly fireDirection = new THREE.Vector3();
  private readonly fireOrigin = new THREE.Vector3();
  private readonly enemyFireDirection = new THREE.Vector3();
  private readonly enemyFireOrigin = new THREE.Vector3();
  private readonly enemyPlayerPoint = new THREE.Vector3();
  private readonly enemyBasePoint = new THREE.Vector3(FIELD.baseX, 0.1, FIELD.baseZ);
  private readonly enemyBaseTargetPoint = new THREE.Vector3(FIELD.baseX, 0.82, FIELD.baseZ);
  private readonly enemyAim = new THREE.Vector3();
  private readonly viewportPoint = new THREE.Vector3();
  private readonly pointer = new THREE.Vector2();
  private readonly enemies: Enemy[] = [];
  private readonly bullets: PlayerProjectile[] = [];
  private readonly enemyBullets: EnemyProjectile[] = [];
  private readonly obstacles: Obstacle[] = [];
  private readonly combo = new ComboSystem();
  private readonly prefersReducedMotion = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  private initialized = false;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private cameraController!: CameraController;
  private gridMovement!: GridMovementSystem;
  private renderer!: THREE.WebGLRenderer;
  private clock!: THREE.Clock;
  private player!: TankGroup;
  private playerShieldVisual!: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  private baseCore!: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial>;
  private baseShield!: THREE.Mesh<THREE.ShapeGeometry, THREE.MeshBasicMaterial>;
  private baseHealthBar!: { width: number; fill: THREE.Sprite };
  private navigation!: NavigationSystem;
  private environment!: EnvironmentSystem;
  private effects!: EffectsSystem;
  private dropSystem!: DropSystem;
  private persistenceCooldown = 1;
  private resetting = false;
  private waveIntermission = 0;
  private waveCountdownSecond = -1;
  private cameraTrauma = 0;
  private shakePhase = 0;
  private uiUpdateCooldown = 0;

  constructor() {
    this.ui = new UIController({
      onStart: this.start,
      onRestart: this.restart,
      onPause: this.togglePause,
      onSound: this.toggleSound,
      onMusicChange: (track) => { this.state.musicTrack = track; this.audio.setMusicTrack(track); this.ui.setMusicTrack(track); this.audio.play('ui'); this.persistProgress(); },
      onVolumeChange: (kind, value) => {
        if (kind === 'music') { this.state.musicVolume = value; this.audio.setMusicVolume(value); }
        else { this.state.soundVolume = value; this.audio.setSoundVolume(value); }
        this.persistProgress();
      },
      onDifficultyChange: (difficulty) => { this.state.difficulty = difficulty; this.ui.setDifficulty(difficulty); this.persistProgress(); this.updateUI(); },
      onCameraChange: (key, value) => { CAMERA_SETTINGS[key] = value; this.state.cameraSettings[key] = value; this.ui.updateCamera(CAMERA_SETTINGS); this.updateCamera(0); this.persistProgress(); },
      onWeapon: this.chooseWeapon,
      onUpgrade: this.buyUpgrade,
    });
    this.restoreProgress();
    this.audio.setMusicVolume(this.state.musicVolume);
    this.audio.setSoundVolume(this.state.soundVolume);
    this.audio.setMusicTrack(this.state.musicTrack);
    if (!this.state.sound) this.audio.toggle();
    new InputController(this.state.input, { onFire: this.firePlayer, onPause: this.togglePause, onSound: this.toggleSound, onPointer: this.updateAimFromPointer, onWeaponSlot: this.chooseWeaponSlot, onSuspend: this.pauseForInterruption });
    this.ui.updateCamera(CAMERA_SETTINGS);
    this.ui.setMusicTrack(this.state.musicTrack);
    this.ui.setSound(this.state.sound);
    this.updateUI();
    window.addEventListener('beforeunload', () => { if (!this.resetting) this.persistProgress(); });
    if (this.state.completed || this.state.victory) this.ui.showVictory();
    else if (this.state.gameOver) this.ui.showGameOver();
  }

  private restoreProgress(): void {
    const saved = loadProgress();
    if (!saved) return;
    Object.assign(this.state, saved);
    this.state.running = false;
    this.state.paused = false;
    this.state.wave = 1;
    this.state.waveTotal = Math.min(7, 3 + this.state.level);
    this.state.waveStarted = false;
    this.state.baseMaxHp = 100 + this.state.upgrades.shield * 22;
    this.state.baseHp = this.state.baseMaxHp;
    this.state.playerHp = this.state.playerMaxHp;
    Object.assign(CAMERA_SETTINGS, this.state.cameraSettings);
    if (this.state.completed) this.state.victory = true;
  }

  private persistProgress(): void {
    saveProgress(snapshotFromState(this.state).data);
  }

  private readonly restart = (): void => {
    this.resetting = true;
    clearProgress();
    window.location.reload();
  };

  private readonly start = (): void => {
    if (this.state.gameOver || this.state.victory || this.state.running) return;
    if (!this.initialized) this.setupScene();
    this.state.running = true;
    this.state.paused = false;
    this.state.startedAt = performance.now();
    this.ui.setGameStarted();
    this.audio.startMusic();
    this.ui.setMusicLive();
    this.ui.showAlert('СИСТЕМА ИЩЕТ ЦЕЛИ...');
    this.beginWaveIntermission(2.6);
    this.renderLoop();
  };

  private setupScene(): void {
    const mount = element<HTMLElement>('#gameCanvas');
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0b171a);
    this.scene.fog = null;
    this.camera = new THREE.PerspectiveCamera(CAMERA_SETTINGS.fov, 1, 0.1, 100);
    this.cameraController = new CameraController(this.camera);
    this.cameraController.initialize(CAMERA_SETTINGS);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.28;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(this.renderer.domElement);
    this.clock = new THREE.Clock();
    this.resizeRenderer();
    window.addEventListener('resize', this.resizeRenderer);

    const hemisphere = new THREE.HemisphereLight(0x9adbd0, 0x24342d, 2.35);
    const ambient = new THREE.AmbientLight(0x9bb8aa, 0.65);
    const key = new THREE.DirectionalLight(0xc2f4f2, 3.15);
    key.position.set(-4, 14, 8);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    const fill = new THREE.DirectionalLight(0x83a99e, 1.75);
    fill.position.set(14, 21, -18);
    this.scene.add(hemisphere, ambient, key, fill);
    const orangeLight = new THREE.PointLight(PALETTE.orange, 12, 18);
    orangeLight.position.set(0, 2.2, 7);
    const cyanLight = new THREE.PointLight(PALETTE.cyan, 6, 15);
    cyanLight.position.set(0, 1.8, -5);
    this.scene.add(orangeLight, cyanLight);

    const outerGround = new THREE.Mesh(new THREE.PlaneGeometry(84, 84), new THREE.MeshStandardMaterial({ color: 0x1a1b1c, roughness: 1, metalness: 0 }));
    outerGround.rotation.x = -Math.PI / 2;
    outerGround.position.y = -0.08;
    outerGround.receiveShadow = true;
    this.scene.add(outerGround);
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(FIELD.width, FIELD.depth), new THREE.MeshStandardMaterial({ map: MAPS.desert, color: 0xb1baac, emissive: 0x30483b, emissiveMap: MAPS.desert, emissiveIntensity: 0.28, roughness: 0.98, metalness: 0.12 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);
    const grid = new THREE.GridHelper(FIELD.width, GRID_COLS, 0x24555d, 0x153039);
    grid.position.y = 0.025;
    const gridMaterial = grid.material as THREE.LineBasicMaterial;
    gridMaterial.transparent = true;
    gridMaterial.opacity = 0.42;
    this.scene.add(grid);
    const environmentGroup = new THREE.Group();
    this.scene.add(environmentGroup);
    createFieldFrame(this.scene);
    this.obstacles.push(...createObstacles(this.scene));
    this.environment = new EnvironmentSystem({ scene: this.scene, ground, outerGround, grid, group: environmentGroup, lights: { hemisphere, ambient, key, fill }, obstacles: this.obstacles });
    this.navigation = new NavigationSystem(
      this.obstacles,
      () => ({ col: this.state.gridCol, row: this.state.gridRow }),
      (col, row) => this.environment.current.riverCells.some((cell) => cell.col === col && cell.row === row),
      (col, row) => this.environment.current.bridgeCells.some((cell) => cell.col === col && cell.row === row),
    );

    const base = createBase();
    base.group.position.set(FIELD.baseX, 0, FIELD.baseZ);
    this.baseCore = base.core;
    this.baseShield = base.shield;
    this.baseShield.visible = false;
    this.baseHealthBar = base.healthBar;
    this.scene.add(base.group);
    const spawn = this.navigation.findSafePlayerSpawn();
    this.state.gridCol = spawn.col;
    this.state.gridRow = spawn.row;
    const spawnPoint = cellToWorld(spawn.col, spawn.row);
    this.state.playerX = spawnPoint.x;
    this.state.playerZ = spawnPoint.z;
    this.state.aimX = spawnPoint.x;
    this.state.aimZ = FIELD.top;
    this.state.aimHeading = this.state.playerHeading;
    this.player = createTank(true);
    this.player.position.set(spawnPoint.x, 0.1, spawnPoint.z);
    this.player.rotation.y = this.state.playerHeading;
    this.gridMovement = new GridMovementSystem({ stepTime: GRID_STEP_TIME });
    this.scene.add(this.player);
    this.playerShieldVisual = new THREE.Mesh(new THREE.RingGeometry(1.08, 1.17, 32), new THREE.MeshBasicMaterial({ color: PALETTE.cyan, transparent: true, opacity: 0.7, side: THREE.DoubleSide }));
    this.playerShieldVisual.rotation.x = -Math.PI / 2;
    this.playerShieldVisual.position.y = 0.08;
    this.player.add(this.playerShieldVisual);
    this.effects = new EffectsSystem(this.scene);
    this.dropSystem = new DropSystem({
      scene: this.scene,
      getPlayerPosition: () => this.player?.position ?? new THREE.Vector3(),
      onCollect: this.collectDrop,
    });
    this.environment.apply(this.state.level);
    this.cameraController.snap(CAMERA_SETTINGS);
    this.initialized = true;
  }

  private readonly resizeRenderer = (): void => {
    if (!this.initialized && !this.renderer) return;
    const mount = element<HTMLElement>('#gameCanvas');
    const width = Math.max(1, mount.clientWidth);
    const height = Math.max(1, mount.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };

  private updateCamera(dt: number): void {
    if (!this.cameraController) return;
    this.cameraController.update(dt, CAMERA_SETTINGS);
  }

  private triggerCameraShake(amount: number): void {
    if (this.prefersReducedMotion) return;
    this.cameraTrauma = Math.max(this.cameraTrauma, THREE.MathUtils.clamp(amount, 0, 1));
  }

  private applyCameraShake(dt: number): void {
    if (this.cameraTrauma <= 0 || this.prefersReducedMotion) return;
    this.shakePhase += dt * 42;
    const strength = this.cameraTrauma * this.cameraTrauma;
    this.camera.position.x += Math.sin(this.shakePhase * 1.7) * strength * 0.28;
    this.camera.position.y += Math.sin(this.shakePhase * 2.3 + 1.2) * strength * 0.16;
    this.cameraTrauma = Math.max(0, this.cameraTrauma - dt * 1.8);
  }

  private projectToViewport(position: THREE.Vector3): { x: number; y: number } {
    const projected = this.viewportPoint.copy(position).project(this.camera);
    return {
      x: (projected.x + 1) * 50,
      y: (1 - projected.y) * 50,
    };
  }

  private readonly renderLoop = (): void => {
    window.requestAnimationFrame(this.renderLoop);
    const dt = Math.min(this.clock.getDelta(), 0.05);
    if (this.state.running && !this.state.paused && !this.state.gameOver) {
      this.state.elapsed += dt;
      this.combo.update(dt);
      this.persistenceCooldown -= dt;
      if (this.persistenceCooldown <= 0) { this.persistProgress(); this.persistenceCooldown = 1; }
      this.state.fireCooldown = Math.max(0, this.state.fireCooldown - dt);
      if (this.state.input.fireHeld) this.firePlayer();
      this.state.playerShieldTime = Math.max(0, this.state.playerShieldTime - dt);
      this.state.baseShieldTime = Math.max(0, this.state.baseShieldTime - dt);
      this.updatePlayer(dt);
      this.updateWaves(dt);
      this.updateEnemies(dt);
      this.updateBullets(dt);
      this.updateDrops(dt);
      this.effects.update(dt);
      this.playerShieldVisual.visible = this.state.playerShieldTime > 0;
      this.playerShieldVisual.material.opacity = 0.52 + Math.sin(this.state.elapsed * 8) * 0.18;
      this.baseShield.visible = this.state.baseShieldTime > 0;
      this.baseShield.material.color.setHex(this.state.baseShieldTime > 0 ? PALETTE.cyan : PALETTE.orange);
      this.baseShield.material.opacity = 0.66 + Math.sin(this.state.elapsed * 5) * 0.14;
      this.uiUpdateCooldown -= dt;
      if (this.uiUpdateCooldown <= 0) {
        this.ui.updateClock(this.state.elapsed, this.state.aimX, FIELD.width);
        this.updateUI();
      }
    }
    if (this.initialized) {
      this.updateCamera(dt);
      this.applyCameraShake(dt);
      this.updateHealthBars();
    }
    this.renderer.render(this.scene, this.camera);
  };

  private updatePlayer(dt: number): void {
    const movement = this.gridMovement.update(
      this.player,
      { col: this.state.gridCol, row: this.state.gridRow },
      dt,
      this.state.input.order?.at(-1),
      (col, row) => !this.navigation.isCellBlocked(col, row) && !this.isCellReservedByTank(col, row),
    );
    this.state.gridCol = movement.cell.col;
    this.state.gridRow = movement.cell.row;
    this.state.gridMove = movement.move;
    this.audio.setEngineActive(Boolean(movement.move));
    this.aimVector.set(this.state.aimX - this.player.position.x, 0, this.state.aimZ - this.player.position.z);
    if (this.aimVector.lengthSq() > 0.05) this.state.aimHeading = Math.atan2(-this.aimVector.x, -this.aimVector.z);
    this.player.position.y = 0.1;
    this.player.rotation.y = this.state.playerHeading;
    this.player.rotation.z = movement.move ? movement.move.vector[0] * -0.018 : 0;
    this.player.userData.weaponMount.rotation.y = this.state.aimHeading - this.state.playerHeading;
    this.state.playerX = this.player.position.x;
    this.state.playerZ = this.player.position.z;
    this.player.userData.recoil = Math.max(0, (this.player.userData.recoil ?? 0) - dt);
    this.player.position.y = 0.1 - this.player.userData.recoil * 0.15;
  }

  private readonly updateAimFromPointer = (event: PointerEvent): void => {
    if (!this.initialized) return;
    const rect = element<HTMLElement>('#gameFrame').getBoundingClientRect();
    this.pointer.x = (event.clientX - rect.left) / rect.width * 2 - 1;
    this.pointer.y = -(event.clientY - rect.top) / rect.height * 2 + 1;
    this.aimRaycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.aimRaycaster.ray.intersectPlane(this.aimPlane, this.aimPoint);
    if (!hit) return;
    this.state.aimX = THREE.MathUtils.clamp(this.aimPoint.x, FIELD.left - 3, FIELD.right + 3);
    this.state.aimZ = THREE.MathUtils.clamp(this.aimPoint.z, FIELD.top - 3, FIELD.bottom + 3);
    this.aimVector.set(this.state.aimX - this.player.position.x, 0, this.state.aimZ - this.player.position.z);
    if (this.aimVector.lengthSq() > 0.05) this.state.aimHeading = Math.atan2(-this.aimVector.x, -this.aimVector.z);
  };

  private readonly firePlayer = (): void => {
    if (!this.initialized || !this.state.running || this.state.paused || this.state.gameOver || this.state.fireCooldown > 0) return;
    const config = WEAPONS[this.state.weapon];
    const damage = config.damage + this.state.upgrades.power * 0.42;
    this.state.fireCooldown = config.cooldown * (1 - this.state.upgrades.cooling * 0.12);
    this.fireDirection.set(0, 0, -1).applyAxisAngle(this.worldUp, this.state.aimHeading).normalize();
    this.fireOrigin.copy(this.player.position).addScaledVector(this.fireDirection, 1.02);
    this.fireOrigin.y = 0.92;
    let mesh: THREE.Mesh;
    if (config.kind === 'rocket') {
      mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.45, 8), makeMaterial(config.color, config.color, 0.8));
      mesh.rotation.x = Math.PI / 2;
      mesh.rotation.z = this.state.aimHeading;
    } else {
      const geometry = config.kind === 'flame' ? new THREE.SphereGeometry(0.16, 8, 8) : new THREE.SphereGeometry(0.105, 8, 8);
      mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: config.color, transparent: true, opacity: 0.95 }));
      const trail = new THREE.PointLight(config.color, config.kind === 'flame' ? 3 : 4, config.kind === 'flame' ? 2.7 : 3);
      mesh.add(trail);
    }
    mesh.position.copy(this.fireOrigin);
    this.scene.add(mesh);
    this.bullets.push({
      mesh,
      velocity: this.fireDirection.clone().multiplyScalar(config.speed),
      previousPosition: this.fireOrigin.clone(),
      damage,
      kind: config.kind,
      life: 2.4,
    });
    this.player.userData.recoil = 0.09;
    this.triggerCameraShake(config.kind === 'rocket' ? 0.2 : config.kind === 'electro' ? 0.04 : 0.03);
    this.audio.play(config.kind === 'rocket' ? 'rocket' : config.kind === 'flame' ? 'flame' : 'shot');
  };

  private createEnemy(type: EnemyType): Enemy {
    const mesh = createTank(false, type);
    const spawn = this.findFreeSpawnCell();
    const col = spawn.col;
    const row = spawn.row;
    const spawnPoint = cellToWorld(col, row);
    mesh.position.set(spawnPoint.x, 0.1, spawnPoint.z);
    this.scene.add(mesh);
    const heavy = type === 'heavy';
    const launcher = type === 'launcher';
    const bulldozer = type === 'bulldozer';
    const difficulty = this.difficultyConfig();
    const hp = bulldozer ? 14 + this.state.level * 2 : launcher ? 5 + this.state.level : heavy ? 6 + this.state.level : 2 + Math.floor(this.state.level / 2);
    const enemy: Enemy = {
      mesh, type, hp, maxHp: hp,
      cooldown: ((launcher ? 1.8 : 1.15) + Math.random() * (launcher ? 1.4 : 1.2)) * difficulty.enemyFireCooldownMultiplier,
      reward: bulldozer ? 80 + this.state.level * 7 : launcher ? 60 + this.state.level * 5 : heavy ? 45 + this.state.level * 4 : 22 + this.state.level * 2,
      phase: Math.random() * Math.PI * 2, gridCol: col, gridRow: row, path: [], pathIndex: 0, routePhase: 'explore', exploreTarget: null, exploreWait: 0, move: null, repath: 0,
      moveTime: bulldozer ? ENEMY_BULLDOZER_MOVE_TIME : launcher ? ENEMY_LAUNCHER_MOVE_TIME : heavy ? ENEMY_HEAVY_MOVE_TIME : ENEMY_SCOUT_MOVE_TIME, heading: 0, turretHeading: 0, aggro: 0, playerAwareness: 0, hitFlash: 0,
    };
    this.enemies.push(enemy);
    return enemy;
  }

  private enemyFire(enemy: Enemy, target: TargetType): void {
    this.enemyFireDirection.set(0, 0, 1).applyAxisAngle(this.worldUp, enemy.turretHeading).normalize();
    this.enemyFireOrigin.copy(enemy.mesh.position).addScaledVector(this.enemyFireDirection, 0.76);
    this.enemyFireOrigin.y = 0.74;
    const launcher = enemy.type === 'launcher';
    const shot = new THREE.Mesh(new THREE.SphereGeometry(launcher ? 0.14 : 0.1, 7, 7), new THREE.MeshBasicMaterial({ color: target === 'base' ? PALETTE.orange : launcher ? 0xb889f2 : 0x6297b5 }));
    shot.position.copy(this.enemyFireOrigin);
    this.scene.add(shot);
    const difficulty = this.difficultyConfig();
    const speed = (launcher ? 4.3 : 3.6) + this.state.level * (launcher ? 0.22 : 0.18);
    const damage = target === 'base'
      ? (launcher ? 9 + this.state.level * 0.6 : 5 + this.state.level * 0.35)
      : (launcher ? 8 + this.state.level * 0.5 : 6 + this.state.level * 0.4);
    this.enemyBullets.push({
      mesh: shot,
      velocity: this.enemyFireDirection.clone().multiplyScalar(speed * difficulty.enemyProjectileSpeedMultiplier),
      previousPosition: this.enemyFireOrigin.clone(),
      damage,
      target,
      life: 4,
    });
    this.audio.play('enemyShot');
  }

  private findFreeSpawnCell(): { col: number; row: number } {
    const configuredPoints = this.environment?.current.enemySpawnPoints ?? [];
    for (let attempt = 0; attempt < Math.max(40, configuredPoints.length * 8); attempt++) {
      const configured = configuredPoints.length > 0
        ? configuredPoints[Math.floor(Math.random() * configuredPoints.length)]
        : null;
      const candidate = configured ?? { col: Math.floor(Math.random() * GRID_COLS), row: Math.floor(Math.random() * 2) };
      if (!this.navigation.isCellBlocked(candidate.col, candidate.row) && !this.isCellReservedByTank(candidate.col, candidate.row)) return candidate;
    }
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        if (!this.navigation.isCellBlocked(col, row) && !this.isCellReservedByTank(col, row)) return { col, row };
      }
    }
    return configuredPoints[0] ?? { col: 0, row: 0 };
  }

  /** Occupancy is kept in the runtime layer until an authoritative multiplayer simulation replaces it. */
  private isCellReservedByTank(col: number, row: number, ignoredEnemy?: Enemy): boolean {
    const playerX = this.player?.position.x ?? this.state.playerX;
    const playerZ = this.player?.position.z ?? this.state.playerZ;
    const cellX = FIELD.left + (col + 0.5) * CELL_SIZE;
    const cellZ = FIELD.top + (row + 0.5) * CELL_SIZE;
    const playerDeltaX = cellX - playerX;
    const playerDeltaZ = cellZ - playerZ;
    if ((this.state.gridCol === col && this.state.gridRow === row)
      || playerDeltaX * playerDeltaX + playerDeltaZ * playerDeltaZ < 3.24) return true;
    for (const enemy of this.enemies) {
      if (enemy === ignoredEnemy) continue;
      if (enemy.gridCol === col && enemy.gridRow === row) return true;
      if (!enemy.move) continue;
      const targetCol = THREE.MathUtils.clamp(Math.floor((enemy.move.to.x - FIELD.left) / CELL_SIZE), 0, GRID_COLS - 1);
      const targetRow = THREE.MathUtils.clamp(Math.floor((enemy.move.to.z - FIELD.top) / CELL_SIZE), 0, GRID_ROWS - 1);
      if (targetCol === col && targetRow === row) return true;
    }
    return false;
  }

  private resolveTankCollisions(): void {
    for (let first = 0; first < this.enemies.length; first++) {
      const firstEnemy = this.enemies[first];
      const a = firstEnemy.mesh.position;
      for (let second = first + 1; second < this.enemies.length; second++) {
        const secondEnemy = this.enemies[second];
        const b = secondEnemy.mesh.position;
        const deltaX = a.x - b.x;
        const deltaZ = a.z - b.z;
        const distanceSq = deltaX * deltaX + deltaZ * deltaZ;
        const minimum = firstEnemy.type === 'heavy' || secondEnemy.type === 'heavy' ? 1.32 : 1.18;
        if (distanceSq >= minimum * minimum) continue;
        const distance = Math.sqrt(distanceSq);
        const directionX = distance > 0.001 ? deltaX / distance : 1;
        const directionZ = distance > 0.001 ? deltaZ / distance : 0;
        const correction = (minimum - distance) / 2;
        a.x += directionX * correction;
        a.z += directionZ * correction;
        b.x -= directionX * correction;
        b.z -= directionZ * correction;
        firstEnemy.move = null;
        secondEnemy.move = null;
      }
    }
  }

  private updateDrops(dt: number): void {
    this.state.dropCooldown -= dt;
    if (this.state.dropCooldown <= 0 && this.dropSystem.active.length < 2) {
      const target = this.findFreeDropTarget();
      if (target) {
        const types: DropType[] = ['medkit', 'shield', 'baseShield', 'credits'];
        const type = types[Math.floor(Math.random() * types.length)];
        this.dropSystem.spawn(type, target);
        this.ui.showAlert(`ДЕСАНТ // ${this.dropName(type)} прибыл`, true);
      }
      this.state.dropCooldown = DROP_MIN_INTERVAL + Math.random() * (DROP_MAX_INTERVAL - DROP_MIN_INTERVAL);
    }
    this.dropSystem.update(dt);
  }

  private findFreeDropTarget(): THREE.Vector3 | null {
    const basePoint = new THREE.Vector3(FIELD.baseX, 0, FIELD.baseZ);
    for (let attempt = 0; attempt < 80; attempt++) {
      const col = 1 + Math.floor(Math.random() * (GRID_COLS - 2));
      const row = 1 + Math.floor(Math.random() * (GRID_ROWS - 3));
      if (this.navigation.isCellBlocked(col, row) || this.isCellReservedByTank(col, row)) continue;
      const target = cellToWorld(col, row);
      if (target.distanceTo(basePoint) < 3) continue;
      if (this.dropSystem.active.some((drop) => drop.target.distanceTo(target) < 2.1)) continue;
      return target;
    }
    return null;
  }

  private readonly collectDrop = (drop: Drop): void => {
    if (drop.type === 'medkit') {
      this.state.playerHp = Math.min(this.state.playerMaxHp, this.state.playerHp + 34);
      this.ui.showAlert('АПТЕЧКА // ПРОЧНОСТЬ ВОССТАНОВЛЕНА', true);
    } else if (drop.type === 'shield') {
      this.state.playerShieldTime = 9;
      this.ui.showAlert('ЭНЕРГОЩИТ // СНАРЯДЫ НЕ НАНОСЯТ УРОН', true);
    } else if (drop.type === 'baseShield') {
      this.state.baseShieldTime = BASE_SHIELD_DURATION;
      this.ui.showAlert(`ЩИТ БАЗЫ // CORE ЗАЩИЩЁН ${BASE_SHIELD_DURATION} СЕКУНД`, true);
    } else {
      this.state.credits += 75 + this.state.level * 10;
      this.ui.showAlert(`РЕСУРСНЫЙ КОНТЕЙНЕР // +₡${75 + this.state.level * 10}`, true);
    }
    this.state.dropsCollected++;
    this.effects.burst(drop.target.clone().setY(0.65), drop.type === 'medkit' ? PALETTE.green : drop.type === 'shield' || drop.type === 'baseShield' ? PALETTE.cyan : PALETTE.orange, 12);
    this.audio.play('pickup');
    if (drop.type === 'shield' || drop.type === 'baseShield') this.audio.play('shield');
    this.audio.play('upgrade');
    this.updateUI();
  };

  private dropName(type: DropType): string {
    return type === 'medkit' ? 'АПТЕЧКА' : type === 'shield' ? 'ЭНЕРГОЩИТ' : type === 'baseShield' ? 'ЩИТ БАЗЫ' : 'КРЕДИТЫ';
  }

  private updateWaves(dt: number): void {
    if (this.state.levelTransition > 0) {
      this.state.levelTransition -= dt;
      if (this.state.levelTransition <= 0) this.startNextLevel();
      return;
    }
    if (this.waveIntermission > 0) {
      this.waveIntermission = Math.max(0, this.waveIntermission - dt);
      const countdown = Math.ceil(this.waveIntermission);
      if (countdown > 0 && countdown !== this.waveCountdownSecond) {
        this.waveCountdownSecond = countdown;
        this.ui.showCenter(`ВОЛНА ${String(this.state.wave).padStart(2, '0')}`, `КОНТАКТ ЧЕРЕЗ ${countdown}`);
      }
      if (this.waveIntermission > 0) return;
    }
    if (!this.state.waveStarted) {
      this.state.waveStarted = true;
      this.state.spawned = 0;
      this.state.waveKills = 0;
      this.state.spawnCooldown = 0.35;
      this.ui.showAlert(`ВОЛНА ${String(this.state.wave).padStart(2, '0')} // ДОСТУП ПОЛУЧЕН`, true);
      this.ui.showCenter('БОЕВОЙ КОНТАКТ', `ВОЛНА ${String(this.state.wave).padStart(2, '0')}`);
    }
    if (this.state.spawned < this.waveSize()) {
      this.state.spawnCooldown -= dt;
      if (this.state.spawnCooldown <= 0) {
        const type = this.enemyTypeForSpawn();
        this.createEnemy(type);
        this.state.spawned++;
        if (type === 'launcher') this.ui.showAlert('РАКЕТНАЯ УСТАНОВКА // ВОЛНА УСИЛЕНА', true);
        if (type === 'bulldozer') this.ui.showAlert('БУЛЬДОЗЕР // ПРЕПЯТСТВИЯ НЕ ОСТАНОВЯТ', true);
        this.state.spawnCooldown = Math.max(0.44, 1.05 - this.state.level * 0.055);
      }
    } else if (this.enemies.length === 0) {
      if (this.state.wave < this.state.waveTotal) {
        const clearedWave = this.state.wave;
        this.state.wave++;
        this.state.waveStarted = false;
        this.state.spawned = 0;
        this.state.waveKills = 0;
        this.beginWaveIntermission();
        this.ui.showAlert(`ВОЛНА ${String(clearedWave).padStart(2, '0')} ЗАЧИЩЕНА // ПЕРЕГРУППИРОВКА`, true);
      } else this.completeLevel();
    }
  }

  private beginWaveIntermission(duration = WAVE_INTERMISSION_TIME, preserveCurrentTransmission = false): void {
    this.waveIntermission = Math.max(0, duration);
    this.waveCountdownSecond = preserveCurrentTransmission ? Math.ceil(this.waveIntermission) : -1;
  }

  private waveSize(): number {
    const baseSize = 4 + this.state.level + (this.state.wave - 1) * 2;
    return Math.max(1, Math.round(baseSize * this.difficultyConfig().enemyCountMultiplier));
  }

  private enemyTypeForSpawn(): EnemyType {
    const slot = this.state.spawned;
    if (this.state.level >= 3 && ((this.state.wave % 3 === 0 && slot === 0) || (slot > 0 && slot % 9 === 8))) return 'bulldozer';
    if (this.state.level >= 2 && ((this.state.wave % 2 === 0 && slot === 0) || (slot > 0 && slot % 7 === 6))) return 'launcher';
    return slot % Math.max(4, 6 - Math.floor(this.state.level / 2)) === 3 ? 'heavy' : 'scout';
  }

  private planEnemyPath(enemy: Enemy): void {
    if (enemy.routePhase === 'explore') {
      const target = this.findEnemyExploreTarget(enemy);
      if (target) {
        const explorationPath = this.navigation.findPathToCell(enemy, target);
        if (explorationPath.length > 0) {
          enemy.path = explorationPath;
          enemy.pathIndex = 0;
          enemy.exploreTarget = target;
          enemy.repath = 0.55;
          return;
        }
      }
      enemy.routePhase = 'base';
      enemy.exploreTarget = null;
    }
    enemy.path = this.navigation.findPathToBase(enemy);
    enemy.pathIndex = 0;
    enemy.repath = 0.55;
  }

  private findEnemyExploreTarget(enemy: Enemy): { col: number; row: number } | null {
    const current = { col: enemy.gridCol, row: enemy.gridRow };
    const candidates = (this.environment?.current.enemyExplorePoints ?? []).filter((candidate) => {
      if (candidate.col === current.col && candidate.row === current.row) return false;
      if (this.navigation.isCellBlocked(candidate.col, candidate.row)) return false;
      if (this.isCellReservedByTank(candidate.col, candidate.row, enemy)) return false;
      if (this.enemies.some((other) => other !== enemy
        && other.routePhase === 'explore'
        && other.exploreTarget?.col === candidate.col
        && other.exploreTarget?.row === candidate.row)) return false;
      return Math.abs(candidate.col - current.col) + Math.abs(candidate.row - current.row) >= 3;
    });
    if (candidates.length === 0) return null;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  private advanceEnemy(enemy: Enemy, dt: number, moveTimeMultiplier = this.difficultyConfig().enemyMoveTimeMultiplier): boolean {
    if (!enemy.move) {
      const next = enemy.path[enemy.pathIndex];
      if (!next) return false;
      if (next.col === this.state.gridCol && next.row === this.state.gridRow) { enemy.repath = 0.12; return false; }
      const target = cellToWorld(next.col, next.row);
      if (this.navigation.isCellBlocked(next.col, next.row) && !this.breakObstacleForBulldozer(enemy, next.col, next.row)) {
        enemy.repath = 0.08;
        enemy.path = [];
        enemy.exploreTarget = null;
        return false;
      }
      if (this.navigation.isCellBlocked(next.col, next.row) || this.isCellReservedByTank(next.col, next.row, enemy)) {
        enemy.repath = 0.08;
        enemy.path = [];
        enemy.exploreTarget = null;
        return false;
      }
      enemy.move = { from: enemy.mesh.position.clone(), to: target, progress: 0 };
      enemy.heading = Math.atan2(target.x - enemy.mesh.position.x, target.z - enemy.mesh.position.z);
    }
    enemy.move.progress = Math.min(1, enemy.move.progress + dt / (enemy.moveTime * moveTimeMultiplier));
    enemy.mesh.position.lerpVectors(enemy.move.from, enemy.move.to, enemy.move.progress);
    if (enemy.move.progress >= 1) {
      const reached = enemy.path[enemy.pathIndex];
      enemy.mesh.position.copy(enemy.move.to);
      if (reached) { enemy.gridCol = reached.col; enemy.gridRow = reached.row; }
      enemy.pathIndex++;
      enemy.move = null;
      if (enemy.pathIndex >= enemy.path.length && enemy.routePhase === 'explore') {
        enemy.routePhase = 'base';
        enemy.exploreTarget = null;
        enemy.path = [];
        enemy.pathIndex = 0;
        enemy.exploreWait = 0.45 + Math.random() * 0.55;
        enemy.repath = 0.55;
      }
    }
    return true;
  }

  private breakObstacleForBulldozer(enemy: Enemy, col: number, row: number): boolean {
    if (enemy.type !== 'bulldozer') return false;
    const obstacleIndex = this.obstacles.findIndex((obstacle) => obstacle.col === col && obstacle.row === row && obstacle.destructible);
    if (obstacleIndex < 0) return false;
    const obstacle = this.obstacles[obstacleIndex];
    this.scene.remove(obstacle.mesh);
    this.obstacles.splice(obstacleIndex, 1);
    this.effects.burst(new THREE.Vector3(obstacle.x, 0.55, obstacle.z), PALETTE.orange, 18);
    this.audio.play('explode');
    return true;
  }

  private turnEnemyTurret(enemy: Enemy, desiredHeading: number, dt: number, turnMultiplier = this.difficultyConfig().enemyTurretTurnMultiplier): boolean {
    const difference = Math.atan2(Math.sin(desiredHeading - enemy.turretHeading), Math.cos(desiredHeading - enemy.turretHeading));
    const turnSpeed = (enemy.type === 'heavy' ? 2.8 : 3.8) * turnMultiplier;
    enemy.turretHeading += THREE.MathUtils.clamp(difference, -turnSpeed * dt, turnSpeed * dt);
    enemy.mesh.userData.weaponMount.rotation.y = enemy.turretHeading - enemy.heading;
    return Math.abs(difference) < 0.055;
  }

  private updateEnemies(dt: number): void {
    this.enemyBasePoint.set(FIELD.baseX, 0.1, FIELD.baseZ);
    this.enemyPlayerPoint.set(this.state.playerX, 0.62, this.state.playerZ);
    const basePoint = this.enemyBasePoint;
    const playerPoint = this.enemyPlayerPoint;
    const playerRangeSq = ENEMY_PLAYER_RANGE * ENEMY_PLAYER_RANGE;
    const baseRangeSq = ENEMY_BASE_RANGE * ENEMY_BASE_RANGE;
    const baseCollisionRangeSq = 2.2 * 2.2;
    const difficulty = this.difficultyConfig();
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      enemy.phase += dt * 3;
      enemy.cooldown -= dt;
      enemy.repath -= dt;
      enemy.exploreWait = Math.max(0, enemy.exploreWait - dt);
      enemy.aggro = Math.max(0, enemy.aggro - dt);
      const playerDistanceSq = enemy.mesh.position.distanceToSquared(playerPoint);
      const baseDistanceSq = enemy.mesh.position.distanceToSquared(basePoint);
      const playerVisible = playerDistanceSq <= playerRangeSq && this.navigation.hasLineOfSight(enemy.mesh.position, playerPoint);
      if (playerVisible) enemy.playerAwareness = Math.min(difficulty.enemyReactionDelay, enemy.playerAwareness + dt);
      else enemy.playerAwareness = Math.max(0, enemy.playerAwareness - dt * 1.5);
      const playerTarget = playerDistanceSq <= playerRangeSq && (enemy.aggro > 0 || (playerVisible && enemy.playerAwareness >= difficulty.enemyReactionDelay));
      const baseTarget = !playerTarget && baseDistanceSq <= baseRangeSq && this.navigation.hasLineOfSight(enemy.mesh.position, basePoint);
      let targetType: TargetType | null = null;
      let targetPoint: THREE.Vector3 | null = null;
      if (playerTarget) { targetType = 'player'; targetPoint = playerPoint; }
      else if (baseTarget) { targetType = 'base'; targetPoint = this.enemyBaseTargetPoint; }
      if (targetType && targetPoint) {
        if (enemy.move) this.advanceEnemy(enemy, dt, difficulty.enemyMoveTimeMultiplier);
        this.enemyAim.copy(targetPoint).sub(enemy.mesh.position);
        this.enemyAim.y = 0;
        const desiredHeading = this.enemyAim.lengthSq() > 0.04 ? Math.atan2(this.enemyAim.x, this.enemyAim.z) : enemy.turretHeading;
        const aligned = this.turnEnemyTurret(enemy, desiredHeading, dt, difficulty.enemyTurretTurnMultiplier);
        if (enemy.type !== 'bulldozer' && enemy.cooldown <= 0 && aligned && (targetType !== 'player' || playerVisible)) {
          this.enemyFire(enemy, targetType);
          enemy.cooldown = (1.7 + Math.random() * 1.3) * difficulty.enemyFireCooldownMultiplier;
        }
      } else {
        this.turnEnemyTurret(enemy, enemy.heading, dt, difficulty.enemyTurretTurnMultiplier);
        if (!enemy.move && enemy.exploreWait <= 0 && enemy.repath <= 0) this.planEnemyPath(enemy);
        if (enemy.exploreWait <= 0) this.advanceEnemy(enemy, dt, difficulty.enemyMoveTimeMultiplier);
      }
      enemy.mesh.position.y = 0.1;
      enemy.mesh.rotation.y = enemy.heading;
      enemy.mesh.rotation.z = enemy.move ? Math.sin(enemy.phase) * 0.018 : 0;
      enemy.hitFlash = Math.max(0, enemy.hitFlash - dt);
      enemy.mesh.scale.setScalar(enemy.hitFlash > 0 ? 1.08 : 1);
      if (enemy.mesh.position.distanceToSquared(basePoint) < baseCollisionRangeSq) {
        this.damageBase(enemy.type === 'bulldozer' ? 28 : enemy.type === 'launcher' ? 16 : enemy.type === 'heavy' ? 19 : 10);
        this.removeEnemy(i);
      }
    }
    this.resolveTankCollisions();
  }

  private updateBullets(dt: number): void {
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const bullet = this.bullets[i];
      if (bullet.previousPosition) bullet.previousPosition.copy(bullet.mesh.position);
      bullet.mesh.position.addScaledVector(bullet.velocity, dt);
      bullet.life -= dt;
      let remove = bullet.life <= 0 || bullet.mesh.position.z < FIELD.top - 2 || bullet.mesh.position.z > FIELD.bottom + 2 || bullet.mesh.position.x < FIELD.left - 2 || bullet.mesh.position.x > FIELD.right + 2;
      if (!remove) {
        for (let obstacleIndex = this.obstacles.length - 1; obstacleIndex >= 0; obstacleIndex--) {
          const block = this.obstacles[obstacleIndex];
          const position = bullet.mesh.position;
          if (Math.abs(position.x - block.x) < block.halfWidth + 0.12 && Math.abs(position.z - block.z) < block.halfDepth + 0.12 && position.y < 1.35) {
            this.effects.burst(position, block.destructible ? (bullet.kind === 'rocket' ? PALETTE.orange : PALETTE.cyan) : PALETTE.green, 4);
            remove = true;
            if (block.destructible) {
              block.hp--;
              if (block.hp <= 0) {
                this.scene.remove(block.mesh);
                this.obstacles.splice(obstacleIndex, 1);
                this.effects.burst(new THREE.Vector3(block.x, 0.5, block.z), PALETTE.orange, 14);
              }
            }
            break;
          }
        }
      }
      if (!remove) {
        const bulletPosition = bullet.mesh.position;
        const hitRadius = bullet.kind === 'rocket' ? 1.1 : 0.72;
        for (let enemyIndex = this.enemies.length - 1; enemyIndex >= 0; enemyIndex--) {
          const enemy = this.enemies[enemyIndex];
          if (Math.abs(bulletPosition.x - enemy.mesh.position.x) < hitRadius && Math.abs(bulletPosition.z - enemy.mesh.position.z) < 0.72) {
            this.hitEnemy(enemyIndex, bullet.damage, bullet.kind);
            remove = true;
            break;
          }
        }
      }
      if (remove) { this.scene.remove(bullet.mesh); this.bullets.splice(i, 1); }
    }
    for (let i = this.enemyBullets.length - 1; i >= 0; i--) {
      const bullet = this.enemyBullets[i];
      if (bullet.previousPosition) bullet.previousPosition.copy(bullet.mesh.position);
      bullet.mesh.position.addScaledVector(bullet.velocity, dt);
      bullet.life -= dt;
      const outside = bullet.mesh.position.z > FIELD.bottom + 2 || bullet.mesh.position.z < FIELD.top - 2 || bullet.mesh.position.x < FIELD.left - 2 || bullet.mesh.position.x > FIELD.right + 2;
      if (outside || bullet.life <= 0) { this.scene.remove(bullet.mesh); this.enemyBullets.splice(i, 1); continue; }
      let playerBulletIndex = -1;
      for (let playerIndex = this.bullets.length - 1; playerIndex >= 0; playerIndex--) {
        const playerBullet = this.bullets[playerIndex];
        if (playerBullet.previousPosition && bullet.previousPosition
          && projectilePathsCollide(
            playerBullet.previousPosition,
            playerBullet.mesh.position,
            bullet.previousPosition,
            bullet.mesh.position,
            0.34,
          )) {
          playerBulletIndex = playerIndex;
          break;
        }
      }
      if (playerBulletIndex >= 0) {
        const playerBullet = this.bullets[playerBulletIndex];
        const impact = this.enemyAim.copy(bullet.mesh.position).lerp(playerBullet.mesh.position, 0.5);
        this.effects.burst(impact, PALETTE.cyan, 9);
        this.audio.play('shield');
        this.scene.remove(playerBullet.mesh);
        this.bullets.splice(playerBulletIndex, 1);
        this.scene.remove(bullet.mesh);
        this.enemyBullets.splice(i, 1);
        continue;
      }
      const targetPoint = bullet.target === 'base'
        ? this.enemyBaseTargetPoint
        : this.enemyPlayerPoint.set(this.state.playerX, 0.62, this.state.playerZ);
      const targetRadius = bullet.target === 'base' ? 1.25 : 0.95;
      if (bullet.mesh.position.distanceToSquared(targetPoint) < targetRadius * targetRadius) {
        this.effects.burst(bullet.mesh.position, bullet.target === 'base' ? PALETTE.orange : PALETTE.red, 5);
        if (bullet.target === 'base') this.damageBase(bullet.damage); else this.damagePlayer(bullet.damage);
        this.scene.remove(bullet.mesh);
        this.enemyBullets.splice(i, 1);
      }
    }
  }

  private hitEnemy(index: number, damage: number, kind: WeaponKind): void {
    const enemy = this.enemies[index];
    if (!enemy) return;
    this.viewportPoint.copy(enemy.mesh.position).setY(0.7);
    const markerPosition = this.projectToViewport(this.viewportPoint);
    enemy.hp -= damage;
    enemy.aggro = ENEMY_AGGRO_TIME;
    enemy.cooldown = Math.min(enemy.cooldown, 0.18);
    enemy.hitFlash = 0.1;
    this.effects.burst(enemy.mesh.position.clone().setY(0.7), kind === 'flame' ? PALETTE.orange : PALETTE.cyan, kind === 'rocket' ? 13 : 5);
    this.audio.play('hit');
    const splashKills: Enemy[] = [];
    if (kind === 'rocket') {
      const splashRadiusSq = 1.7 * 1.7;
      for (const other of this.enemies) {
        if (other === enemy || other.mesh.position.distanceToSquared(enemy.mesh.position) >= splashRadiusSq) continue;
        other.hp -= damage * 0.42;
        other.hitFlash = 0.12;
        if (other.hp <= 0) splashKills.push(other);
      }
    }
    let killConfirmed = false;
    if (enemy.hp <= 0) {
      this.destroyEnemy(enemy);
      killConfirmed = true;
    }
    splashKills.forEach((other) => {
      this.destroyEnemy(other);
      killConfirmed = true;
    });
    this.ui.showHitMarker(killConfirmed, markerPosition);
    this.triggerCameraShake(kind === 'rocket' ? 0.38 : 0.15);
  }

  private destroyEnemy(enemy: Enemy): void {
    const index = this.enemies.indexOf(enemy);
    if (index < 0) return;
    const chain = this.combo.registerKill();
    const reward = this.combo.reward(enemy.reward);
    this.state.score++;
    this.state.waveKills++;
    this.state.credits += reward;
    this.effects.burst(enemy.mesh.position.clone().setY(0.65), enemy.type === 'heavy' || enemy.type === 'bulldozer' ? PALETTE.orange : PALETTE.red, enemy.type === 'heavy' || enemy.type === 'bulldozer' ? 23 : 12);
    this.audio.play('explode');
    this.removeEnemy(index);
    if (chain.count === 3 || chain.count === 6 || chain.count === 10) {
      this.ui.showAlert(`СЕРИЯ ${chain.count} // НАГРАДА ×${chain.multiplier}`, true);
    }
  }

  private removeEnemy(index: number): void {
    const enemy = this.enemies[index];
    if (!enemy) return;
    this.scene.remove(enemy.mesh);
    this.enemies.splice(index, 1);
  }

  private damageBase(amount: number): void {
    if (this.state.gameOver) return;
    if (this.state.baseShieldTime > 0) {
      this.effects.burst(new THREE.Vector3(FIELD.baseX, 1, FIELD.baseZ), PALETTE.cyan, 8);
      this.audio.play('shield');
      this.ui.showAlert(`ЩИТ БАЗЫ // УДАР ОТРАЖЁН (${Math.ceil(this.state.baseShieldTime)}С)`, true);
      return;
    }
    this.state.baseHp = Math.max(0, this.state.baseHp - amount);
    this.combo.reset();
    this.baseCore.material.emissiveIntensity = 1.5;
    window.setTimeout(() => { if (this.baseCore) this.baseCore.material.emissiveIntensity = 0.6; }, 120);
    this.effects.burst(new THREE.Vector3(FIELD.baseX, 1, FIELD.baseZ), PALETTE.orange, 14);
    this.ui.flashDamage('base');
    this.triggerCameraShake(0.58);
    this.audio.play('base');
    this.ui.showAlert('ПРЕДУПРЕЖДЕНИЕ // УДАР ПО CORE-A', true);
    if (this.state.baseHp <= 0) this.endGame();
  }

  private damagePlayer(amount: number): void {
    if (this.state.gameOver || this.state.victory || this.state.playerShieldTime > 0) {
      if (this.state.playerShieldTime > 0) {
        this.ui.showAlert('ЭНЕРГОЩИТ // ПОПАДАНИЕ ОТРАЖЕНО');
        this.audio.play('shield');
      }
      return;
    }
    this.state.playerHp = Math.max(0, this.state.playerHp - amount);
    this.combo.reset();
    this.effects.burst(new THREE.Vector3(this.state.playerX, 0.8, this.state.playerZ), PALETTE.red, 7);
    this.ui.flashDamage('player');
    this.triggerCameraShake(0.72);
    this.audio.play('damage');
    this.ui.showAlert('ПРЕДУПРЕЖДЕНИЕ // УДАР ПО ТАНКУ', true);
    if (this.state.playerHp <= 0) this.endGame('player');
  }

  private updateHealthBars(): void {
    this.updateHealthBar(this.player?.userData.healthBar, this.state.playerHp, this.state.playerMaxHp);
    for (const enemy of this.enemies) this.updateHealthBar(enemy.mesh.userData.healthBar, enemy.hp, enemy.maxHp);
    this.updateHealthBar(this.baseHealthBar, this.state.baseHp, this.state.baseMaxHp);
  }

  private updateHealthBar(bar: { width: number; fill: THREE.Sprite } | undefined, value: number, maximum: number): void {
    if (!bar) return;
    const ratio = THREE.MathUtils.clamp(value / Math.max(1, maximum), 0, 1);
    bar.fill.scale.x = bar.width * ratio;
    bar.fill.position.x = -bar.width * (1 - ratio) / 2;
    bar.fill.visible = ratio > 0;
  }

  private completeLevel(): void {
    if (this.state.levelTransition > 0) return;
    const reward = 110 + this.state.level * 40;
    this.state.credits += reward;
    this.state.levelTransition = 1;
    this.audio.setEngineActive(false);
    this.persistProgress();
    this.ui.showAlert(`УРОВЕНЬ ${String(this.state.level).padStart(2, '0')} ЗАЧИЩЕН // +₡${reward}`, true);
    this.ui.showCenter('СЕКТОР ЗАЧИЩЕН', `+${reward} КРЕДИТОВ`);
    this.audio.play('level');
    if (this.state.level >= MAX_LEVEL) {
      this.state.completed = true;
      this.state.victory = true;
      this.state.running = false;
      this.audio.setEngineActive(false);
      this.persistProgress();
      this.ui.showVictory?.();
      return;
    }
    this.state.paused = true;
    this.ui.setPaused(true);
  }

  private startNextLevel(): void {
    if (this.state.gridMove) {
      this.player.position.copy(this.state.gridMove.to);
      this.state.gridMove = null;
      this.gridMovement.reset();
    }
    this.state.level++;
    this.state.wave = 1;
    this.state.waveTotal = Math.min(7, 3 + this.state.level);
    this.state.waveStarted = false;
    this.state.spawned = 0;
    this.state.baseHp = Math.min(this.state.baseMaxHp, this.state.baseHp + 20);
    this.state.playerHp = Math.min(this.state.playerMaxHp, this.state.playerHp + 25);
    this.state.playerShieldTime = 0;
    this.state.baseShieldTime = 0;
    this.state.dropCooldown = 7;
    this.combo.reset();
    this.beginWaveIntermission(WAVE_INTERMISSION_TIME + 1, true);
    this.dropSystem.clear();
    const location = this.environment.apply(this.state.level);
    if (this.navigation.isCellBlocked(this.state.gridCol, this.state.gridRow)) {
      const safeCell = this.navigation.findSafePlayerSpawn();
      const safePoint = cellToWorld(safeCell.col, safeCell.row);
      this.state.gridCol = safeCell.col;
      this.state.gridRow = safeCell.row;
      this.state.playerX = safePoint.x;
      this.state.playerZ = safePoint.z;
      this.player.position.copy(safePoint);
      this.gridMovement.reset();
    }
    this.ui.showCenter(location.kicker, `${location.name} // ${this.difficultyName()}`);
    this.persistProgress();
    this.updateUI();
  }

  private endGame(reason: 'base' | 'player' = 'base'): void {
    this.state.gameOver = true;
    this.state.completed = false;
    this.state.running = false;
    this.combo.reset();
    this.audio.setEngineActive(false);
    this.persistProgress();
    this.ui.showGameOver(reason);
    this.audio.play('gameover');
  }

  private readonly togglePause = (): void => {
    if (this.state.victory || this.state.gameOver) return;
    if (!this.state.running && !this.state.paused) return;
    if (this.state.paused && this.state.levelTransition > 0) {
      this.state.levelTransition = 0;
      this.startNextLevel();
    }
    this.state.paused = !this.state.paused;
    if (this.state.paused) this.audio.setEngineActive(false);
    this.ui.setPaused(this.state.paused);
    if (!this.state.paused) this.clock.getDelta();
  };

  private readonly pauseForInterruption = (): void => {
    if (!this.state.running || this.state.paused || this.state.gameOver || this.state.victory) return;
    this.state.paused = true;
    this.audio.setEngineActive(false);
    this.ui.setPaused(true);
  };

  private readonly toggleSound = (): void => {
    const enabled = this.audio.toggle();
    this.state.sound = enabled;
    this.ui.setSound(enabled);
    this.persistProgress();
  };

  private readonly chooseWeapon = (kind: WeaponKind): void => {
    if (!this.state.unlocked[kind]) {
      const price = WEAPONS[kind].reward;
      if (this.state.credits < price) { this.ui.showAlert(`НЕДОСТАТОЧНО КРЕДИТОВ // НУЖНО ₡${price}`, true); this.audio.play('deny'); return; }
      this.state.credits -= price;
      this.state.unlocked[kind] = true;
      this.persistProgress();
      this.ui.unlockWeapon(kind);
      this.ui.showAlert(`${WEAPONS[kind].display} // МОДУЛЬ УСТАНОВЛЕН`, true);
      this.audio.play('upgrade');
    }
    this.state.weapon = kind;
    this.ui.setWeapon(kind);
    this.persistProgress();
    this.updateUI();
  };

  private readonly chooseWeaponSlot = (slot: 0 | 1 | 2): void => {
    if (!this.state.running || this.state.paused || this.state.gameOver || this.state.victory) return;
    const kind = (['electro', 'flame', 'rocket'] as const)[slot];
    if (!this.state.unlocked[kind]) {
      this.ui.showAlert(`СЛОТ ${slot + 1} ЗАБЛОКИРОВАН // ОТКРОЙТЕ В МЕНЮ ПАУЗЫ`, true);
      this.audio.play('deny');
      return;
    }
    if (this.state.weapon === kind) return;
    this.state.weapon = kind;
    this.ui.setWeapon(kind);
    this.ui.showAlert(`ОРУЖИЕ ${slot + 1} // ${WEAPONS[kind].display}`);
    this.audio.play('ui');
    this.persistProgress();
    this.updateUI();
  };

  private readonly buyUpgrade = (kind: UpgradeKind): void => {
    if (this.state.upgrades[kind] >= 3) return;
    const cost = UPGRADE_BASE_COSTS[kind] + this.state.upgrades[kind] * 55;
    if (this.state.credits < cost) { this.ui.showAlert(`НЕДОСТАТОЧНО КРЕДИТОВ // НУЖНО ₡${cost}`, true); this.audio.play('deny'); return; }
    this.state.credits -= cost;
    this.state.upgrades[kind]++;
    if (kind === 'shield') { this.state.baseMaxHp += 22; this.state.baseHp = Math.min(this.state.baseMaxHp, this.state.baseHp + 22); }
    this.ui.showAlert(`МОДЕРНИЗАЦИЯ ПРИНЯТА // ${kind.toUpperCase()}`, true);
    this.audio.play('upgrade');
    this.persistProgress();
    this.updateUI();
  };

  private updateUI(): void {
    let target: Enemy | undefined;
    let closestDistanceSq = Number.POSITIVE_INFINITY;
    const playerPosition = this.player?.position;
    if (playerPosition) {
      for (const enemy of this.enemies) {
        const distanceSq = enemy.mesh.position.distanceToSquared(playerPosition);
        if (distanceSq < closestDistanceSq) {
          closestDistanceSq = distanceSq;
          target = enemy;
        }
      }
    }
    this.ui.update(this.state, {
      enemyCount: this.enemies.length,
      waveSize: this.waveSize(),
      difficulty: this.difficultyName(),
      location: this.environment ? this.environment.current.name : undefined,
      target: target ? { hp: target.hp, maxHp: target.maxHp, name: this.enemyName(target.type) } : undefined,
      drops: this.dropSystem?.active.map((drop) => ({
        id: drop.id,
        type: drop.type,
        label: this.dropName(drop.type),
        status: drop.landed ? `ДО ИСЧЕЗНОВЕНИЯ ${Math.max(0, Math.ceil(drop.life))}С` : 'ДОСТАВКА В ПУТИ',
      })),
      dropsCollected: this.state.dropsCollected,
      combo: this.combo.snapshot,
    });
    this.uiUpdateCooldown = 0.1;
  }
  private difficultyConfig() { return DIFFICULTY_CONFIG[this.state.difficulty] ?? DIFFICULTY_CONFIG.normal; }
  private difficultyName(): string { return this.difficultyConfig().label; }
  private enemyName(type: EnemyType): string {
    return type === 'heavy' ? 'ТЯЖЁЛЫЙ ТАНК' : type === 'launcher' ? 'РАКЕТНАЯ УСТАНОВКА' : type === 'bulldozer' ? 'БУЛЬДОЗЕР' : 'РАЗВЕДЧИК';
  }
}

function projectilePathsCollide(
  firstStart: THREE.Vector3,
  firstEnd: THREE.Vector3,
  secondStart: THREE.Vector3,
  secondEnd: THREE.Vector3,
  radius: number,
): boolean {
  if (segmentsIntersect(firstStart.x, firstStart.z, firstEnd.x, firstEnd.z, secondStart.x, secondStart.z, secondEnd.x, secondEnd.z)) return true;
  return Math.min(
    pointToSegmentDistance(firstStart.x, firstStart.z, secondStart.x, secondStart.z, secondEnd.x, secondEnd.z),
    pointToSegmentDistance(firstEnd.x, firstEnd.z, secondStart.x, secondStart.z, secondEnd.x, secondEnd.z),
    pointToSegmentDistance(secondStart.x, secondStart.z, firstStart.x, firstStart.z, firstEnd.x, firstEnd.z),
    pointToSegmentDistance(secondEnd.x, secondEnd.z, firstStart.x, firstStart.z, firstEnd.x, firstEnd.z),
  ) <= radius;
}

function segmentsIntersect(
  firstStartX: number,
  firstStartZ: number,
  firstEndX: number,
  firstEndZ: number,
  secondStartX: number,
  secondStartZ: number,
  secondEndX: number,
  secondEndZ: number,
): boolean {
  const firstOrientation = orientation(firstStartX, firstStartZ, firstEndX, firstEndZ, secondStartX, secondStartZ);
  const secondOrientation = orientation(firstStartX, firstStartZ, firstEndX, firstEndZ, secondEndX, secondEndZ);
  const thirdOrientation = orientation(secondStartX, secondStartZ, secondEndX, secondEndZ, firstStartX, firstStartZ);
  const fourthOrientation = orientation(secondStartX, secondStartZ, secondEndX, secondEndZ, firstEndX, firstEndZ);
  return firstOrientation !== secondOrientation && thirdOrientation !== fourthOrientation;
}

function orientation(firstX: number, firstZ: number, secondX: number, secondZ: number, thirdX: number, thirdZ: number): number {
  const value = (secondZ - firstZ) * (thirdX - secondX) - (secondX - firstX) * (thirdZ - secondZ);
  return Math.abs(value) < 1e-6 ? 0 : value > 0 ? 1 : -1;
}

function pointToSegmentDistance(pointX: number, pointZ: number, startX: number, startZ: number, endX: number, endZ: number): number {
  const deltaX = endX - startX;
  const deltaZ = endZ - startZ;
  const lengthSq = deltaX * deltaX + deltaZ * deltaZ;
  const projection = lengthSq > 0 ? Math.max(0, Math.min(1, ((pointX - startX) * deltaX + (pointZ - startZ) * deltaZ) / lengthSq)) : 0;
  const closestX = startX + projection * deltaX;
  const closestZ = startZ + projection * deltaZ;
  return Math.hypot(pointX - closestX, pointZ - closestZ);
}
